import { useState, useEffect, useCallback } from "react";
import API, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const money = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const METHODS = [
  ["bank_transfer", "Bank transfer"], ["paypal", "PayPal"], ["wire", "Wire"],
  ["payoneer", "Payoneer"], ["wise", "Wise"], ["crypto", "Crypto"], ["other", "Other"],
];
const METHOD_LABEL = Object.fromEntries(METHODS);
const monthLabel = (ym) => {
  if (!ym) return "Undated";
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString("en-US", { month: "short", year: "numeric", timeZone: "UTC" });
};
const today = () => new Date().toISOString().slice(0, 10);

const EMPTY = {
  amount_usd: "", paid_on: today(), method: "bank_transfer",
  reference: "", period_start: "", period_end: "", note: "",
};

export default function AdminPublisherPayments({ publisherId }) {
  const [stmt, setStmt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/admin/publishers/${publisherId}/payments`);
      setStmt(data);
    } catch (e) {
      toast.error(formatApiError(e) || "Could not load payments");
    }
    setLoading(false);
  }, [publisherId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount_usd);
    if (!amt || amt <= 0) { toast.error("Enter an amount greater than 0"); return; }
    setSaving(true);
    try {
      const { data } = await API.post(`/admin/publishers/${publisherId}/payments`, {
        ...form, amount_usd: amt,
      });
      if (data?.statement) setStmt(data.statement); else await load();
      setForm({ ...EMPTY });
      toast.success(`Recorded ${money(amt)}`);
    } catch (e2) {
      toast.error(formatApiError(e2) || "Could not record payment");
    }
    setSaving(false);
  };

  const voidPayment = async (p) => {
    const reason = window.prompt(`Void the ${money(p.amount_usd)} payment from ${p.paid_on}? This is kept for the record. Reason (optional):`);
    if (reason === null) return;
    try {
      await API.post(`/admin/payments/${p.payment_id}/void`, { reason });
      toast.success("Payment voided");
      await load();
    } catch (e) {
      toast.error(formatApiError(e) || "Could not void payment");
    }
  };

  const editShare = async () => {
    const cur = stmt?.pub_share_pct ?? 100;
    const input = window.prompt(`Publisher's payout share % of approved commission (1–100). This affects the payout statement only, not earnings.`, String(cur));
    if (input === null) return;
    const pct = parseFloat(input);
    if (!(pct > 0 && pct <= 100)) { toast.error("Share must be between 0 and 100"); return; }
    try {
      const { data } = await API.put(`/admin/publishers/${publisherId}/payout-share`, { pub_share_pct: pct });
      if (data?.statement) setStmt(data.statement); else await load();
      toast.success(`Payout share set to ${pct}%`);
    } catch (e) {
      toast.error(formatApiError(e) || "Could not set share");
    }
  };

  if (loading) return <div className="p-4 text-sm text-slate-500">Loading…</div>;

  const payments = stmt?.payments || [];
  const monthly = stmt?.monthly || [];
  const share = stmt?.pub_share_pct ?? 100;
  const hasCut = share < 100;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-sm">
          Payout share:{" "}
          <button onClick={editShare} className="font-semibold text-[#002FA7] hover:underline">
            {share}% to publisher{hasCut ? ` / ${(100 - share)}% RedClub` : ""}
          </button>
          <span className="text-xs text-slate-500 ml-2">(statement only — earnings unchanged)</span>
        </div>
      </div>
      <div className={`grid grid-cols-1 gap-4 ${hasCut ? "sm:grid-cols-5" : "sm:grid-cols-4"}`}>
        {hasCut && <Stat label="Shown earnings (gross)" value={money(stmt?.gross_earnings_usd)} />}
        <Stat label={hasCut ? `Payable (${share}% share)` : "Shown earnings"} value={money(stmt?.payable_usd ?? stmt?.approved_earnings_usd)} />
        <Stat label="Paid to publisher" value={money(stmt?.total_paid_usd)} />
        <Stat label="Outstanding balance" value={money(stmt?.outstanding_balance_usd)} highlight />
        <Stat label="Payments recorded" value={String(stmt?.payment_count || 0)} />
      </div>
      <div className="text-xs text-slate-500 -mt-3">
        Shown earnings = approved {money(stmt?.gross_approved_usd)} + pending {money(stmt?.gross_pending_usd)} (reversed excluded).
        The publisher sees the same balance and monthly rows, without the RedClub column.
      </div>

      {monthly.length > 0 && (
        <div className="rounded-lg border">
          <div className="px-4 py-3 border-b font-semibold text-sm">By month</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500 border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">Month</th>
                  <th className="px-4 py-2 font-medium text-right">Shown</th>
                  {hasCut && <th className="px-4 py-2 font-medium text-right">Publisher {share}%</th>}
                  {hasCut && <th className="px-4 py-2 font-medium text-right">RedClub {100 - share}%</th>}
                  <th className="px-4 py-2 font-medium text-right">Sent</th>
                  <th className="px-4 py-2 font-medium text-right">Running balance owed</th>
                </tr>
              </thead>
              <tbody>
                {monthly.map((r) => (
                  <tr key={r.month || "undated"} className="border-b">
                    <td className="px-4 py-2 whitespace-nowrap">{monthLabel(r.month)}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums">{money(r.earned_usd)}</td>
                    {hasCut && <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums">{money(r.payable_usd)}</td>}
                    {hasCut && <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums">{money(r.platform_fee_usd)}</td>}
                    <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums">{money(r.paid_usd)}</td>
                    <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums">{money(r.balance_usd)}</td>
                  </tr>
                ))}
                <tr className="font-semibold">
                  <td className="px-4 py-2">Total</td>
                  <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums">{money(stmt?.gross_earnings_usd)}</td>
                  {hasCut && <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums">{money(stmt?.payable_usd)}</td>}
                  {hasCut && <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums">{money(stmt?.platform_fee_usd)}</td>}
                  <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums">{money(stmt?.total_paid_usd)}</td>
                  <td className="px-4 py-2 whitespace-nowrap text-right tabular-nums">{money(stmt?.outstanding_balance_usd)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <form onSubmit={submit} className="rounded-lg border p-4 space-y-3">
        <div className="font-semibold text-sm">Record a payment</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Amount (USD)">
            <input type="number" step="0.01" min="0" required value={form.amount_usd}
              onChange={(e) => setForm({ ...form, amount_usd: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-sm" placeholder="0.00" />
          </Field>
          <Field label="Paid on">
            <input type="date" value={form.paid_on}
              onChange={(e) => setForm({ ...form, paid_on: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Method">
            <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-sm">
              {METHODS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Reference">
            <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-sm" placeholder="txn / invoice #" />
          </Field>
          <Field label="Period from">
            <input type="date" value={form.period_start}
              onChange={(e) => setForm({ ...form, period_start: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Period to">
            <input type="date" value={form.period_end}
              onChange={(e) => setForm({ ...form, period_end: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-sm" />
          </Field>
          <Field label="Note" className="md:col-span-2">
            <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
              className="w-full border rounded px-2 py-1.5 text-sm" placeholder="optional" />
          </Field>
        </div>
        <button type="submit" disabled={saving}
          className="bg-[#002FA7] text-white text-sm font-medium px-4 py-2 rounded disabled:opacity-50">
          {saving ? "Saving…" : "Record payment"}
        </button>
      </form>

      <div className="rounded-lg border">
        <div className="px-4 py-3 border-b font-semibold text-sm">Payment history</div>
        {payments.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-500">No payments recorded yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-slate-500 border-b">
                <tr>
                  <th className="px-4 py-2 font-medium">Date</th>
                  <th className="px-4 py-2 font-medium">Amount</th>
                  <th className="px-4 py-2 font-medium">Method</th>
                  <th className="px-4 py-2 font-medium">Reference</th>
                  <th className="px-4 py-2 font-medium">Period</th>
                  <th className="px-4 py-2 font-medium">Note</th>
                  <th className="px-4 py-2 font-medium">By</th>
                  <th className="px-4 py-2 font-medium"></th>
                </tr>
              </thead>
              <tbody>
                {payments.map((p) => {
                  const isVoid = p.status === "void";
                  return (
                    <tr key={p.payment_id} className={`border-b last:border-0 ${isVoid ? "text-slate-400 line-through" : ""}`}>
                      <td className="px-4 py-2 whitespace-nowrap">{p.paid_on}</td>
                      <td className="px-4 py-2 whitespace-nowrap font-medium">{money(p.amount_usd)}</td>
                      <td className="px-4 py-2 whitespace-nowrap">{METHOD_LABEL[p.method] || p.method}</td>
                      <td className="px-4 py-2">{p.reference || "—"}</td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        {p.period_start || p.period_end ? `${p.period_start || "…"} → ${p.period_end || "…"}` : "—"}
                      </td>
                      <td className="px-4 py-2">{p.note || "—"}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-xs">{p.created_by}</td>
                      <td className="px-4 py-2 whitespace-nowrap text-right">
                        {isVoid ? <span className="text-xs">void</span> : (
                          <button onClick={() => voidPayment(p)}
                            className="text-xs text-red-600 hover:underline">Void</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded-lg border p-4 ${highlight ? "bg-[#002FA7]/5 border-[#002FA7]/30" : ""}`}>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-xl font-bold mt-1">{value}</div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs text-slate-500">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
